import {put, call, all} from 'redux-saga/effects';
import Deadline from "../../entities/deadline";
import * as Actions from "../actions/deadlineActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DeadlineAction = {
    deadlineId: string;
    deadline: Deadline;
    deadlines: Deadline[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDeadline(action: DeadlineAction) {
	const session = tabSession.captureGeneration();
	try {
		const deadline: Deadline = yield call(api.findOne, 'deadline', action.deadlineId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_FETCH_ONE_SUCCESS, deadlineId: action.deadlineId, deadline, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINE_FETCH_ONE_FAILURE, deadlineId: action.deadlineId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DeadlineAction} action
 */
export function* saveDeadline(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DEADLINE_SAVE saga', action.deadline?.id);

		if (action.deadline === undefined || action.deadline === null) {
			console.error('saveDeadline received a null');
			throw new Error('saveDeadline received a null');
		}

		if (!(action.deadline instanceof Deadline)) {
			action.deadline = new Deadline(action.deadline);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.deadline.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.deadline);
			const addResult = yield call(idb.add, 'deadline', action.deadline);
			if (addResult.isErr()) {
				console.error(`⮂ saveDeadline: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertDeadlineSave(action.deadline, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.DEADLINE_SAVE_FAILURE, deadline: action.deadline, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.DEADLINE_SAVE, deadline: action.deadline, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertDeadlineSave(action.deadline, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.DEADLINE_SAVE_FAILURE, deadline: action.deadline, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {DeadlineAction} action
 * @param {Deadline[]} action.deadlines
 */
export function* saveManyDeadlines(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DEADLINE_SAVE_MANY saga', action.deadlines.map(deadline => deadline.id).join(','));

        if (!Array.isArray(action.deadlines)) {
            console.error('saveManyDeadlines expected an array, received', action.deadlines);
            throw new Error('saveManyDeadlines expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveDeadline), under these queue keys
		const dispatchedAddedOns: number[] = action.deadlines.map(deadline => deadline?.addedOn);
		const revertAll = () => action.deadlines.map((deadline, i) =>
			put(Actions.revertDeadlineSave(deadline, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Deadline[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.deadlines.length; i++) {
				const deadline = action.deadlines[i];
				deadline.persisted = false;
				ensureOperationId(deadline);
				toSave.push(deadline);
			}

			const addResult = yield call(idb.add, 'deadline', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyDeadlines: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.DEADLINE_SAVE_MANY_FAILURE, deadlines: action.deadlines, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.DEADLINE_SAVE_MANY, deadlines: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.DEADLINE_SAVE_MANY_FAILURE, deadlines: action.deadlines, status: 500, err});

			console.error(`Error saving ${action.deadlines.length} deadlines to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {DeadlineAction} action
 */
export function* removeDeadline(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.deadline === undefined || action.deadline === null || !(action.deadline instanceof Deadline)) {
			console.error('removeDeadline expected type Deadline, received', action.deadline);
			reportQueued(false);
			throw new Error('removeDeadline expected type Deadline');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.deadline.status = 'D';
		action.deadline.addedOn = newHRTimestamp();
		action.deadline.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.deadline);
		const addResult = yield call(idb.add, 'deadline', action.deadline);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDeadline: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.DEADLINE_REMOVE_FAILURE, deadline: action.deadline, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_REMOVE, deadline: action.deadline});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINE_REMOVE_FAILURE, deadline: action.deadline, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Deadline.
	*
	* @param {DeadlineAction} action
	*/
export function* restoreDeadline(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDeadline expected getCurrentUserId() to return a value');
        return;
    }

	if (action.deadline === undefined || action.deadline === null) {
        console.error('restoreDeadline expected a Deadline, received null');
        throw new Error('restoreDeadline expected a Deadline, received null');
    }
    else if (!(action.deadline instanceof Deadline)) action.deadline = new Deadline(action.deadline); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.deadline.status = 'U';
		action.deadline.addedOn = newHRTimestamp();
		action.deadline.addedBy = userId;
		action.deadline.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.deadline);
		const addResult = yield call(idb.add, 'deadline', action.deadline);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDeadline: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINE_RESTORE_FAILURE, deadline: action.deadline, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_RESTORE, deadline: action.deadline});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINE_RESTORE_FAILURE, deadline: action.deadline, status: 500, err});

		console.error('Error restoring from database', err);
	}
}